// src/pages/subscription/SubscriptionAnalyticsPage.tsx

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useSubscriptionAnalytic, useI18n } from '@react/hooks';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '--';
  return (amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

type Period = '7d' | '30d' | '90d' | '12m';

const PERIOD_OPTIONS: { key: Period; label: string; days: number }[] = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
  { key: '12m', label: '12 months', days: 365 },
];

export const SubscriptionAnalyticsPage = observer(() => {
  const store = useSubscriptionAnalytic();
  const { t } = useI18n();
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('30d');
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);
      try {
        const days = PERIOD_OPTIONS.find((o) => o.key === selectedPeriod)?.days ?? 30;
        const results = await Promise.allSettled([
          store.GetSubscriptionAnalytics({
            period_start: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString(),
            period_end: new Date().toISOString(),
          }),
        ]);
        const res = results[0];
        if (res.status === 'fulfilled') {
          const value = (res.value ?? {}) as any;
          const summary = value.summary ?? {};
          setAnalytics({ ...value, ...summary, mrr: summary.total_mrr });
        } else {
          setAnalytics({});
        }
      } catch {
        setAnalytics({});
      } finally {
        setLoading(false);
      }
    };
    loadAnalytics();
  }, [selectedPeriod]);

  const mrr = analytics?.mrr ?? 0;
  const arr = analytics?.arr ?? 0;
  const activeSubscriptions = analytics?.active_subscriptions ?? 0;
  const churnRate = analytics?.churn_rate ?? 0;
  const newSubscriptions = analytics?.new_subscriptions ?? 0;
  const cancellations = analytics?.cancellations ?? 0;
  const upgrades = analytics?.upgrades ?? 0;
  const downgrades = analytics?.downgrades ?? 0;
  const trialConversionRate = analytics?.trial_conversion_rate ?? 0;
  const arpu = analytics?.arpu ?? 0;
  const revenueByPlanRaw = analytics?.revenue_by_plan?.items ?? analytics?.revenue_by_plan;
  const revenueByPlan = Array.isArray(revenueByPlanRaw) ? revenueByPlanRaw : [];

  const totalRevenue = revenueByPlan.reduce((sum: number, p: any) => sum + (p.revenue ?? 0), 0);

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">{t('subscription.analytics')}</h1>
        <nav className="text-sm">
          <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">
            {t('subscription.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('subscription.analytics')}</span>
        </nav>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Period selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-foreground">{t('subscription.period')}:</label>
            <div className="flex gap-1">
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSelectedPeriod(opt.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    selectedPeriod === opt.key
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stats cards row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">{t('subscription.mrr')}</p>
                <p className="text-2xl font-bold text-foreground">${formatMoney(mrr)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">{t('subscription.arr')}</p>
                <p className="text-2xl font-bold text-foreground">${formatMoney(arr)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">{t('subscription.activeSubscriptions')}</p>
                <p className="text-2xl font-bold text-foreground">{activeSubscriptions}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">{t('subscription.churnRate')}</p>
                <p className="text-2xl font-bold text-foreground">{(churnRate * 100).toFixed(2)}%</p>
              </CardContent>
            </Card>
          </div>

          {/* Metrics cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t('subscription.newSubscriptions')}</p>
                  <Badge variant="success" size="sm">{t('subscription.period')}</Badge>
                </div>
                <p className="text-2xl font-bold text-foreground mt-2">{newSubscriptions}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t('subscription.cancellations')}</p>
                  <Badge variant="error" size="sm">{t('subscription.period')}</Badge>
                </div>
                <p className="text-2xl font-bold text-foreground mt-2">{cancellations}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t('subscription.upgrades')}</p>
                  <Badge variant="success" size="sm">{t('subscription.period')}</Badge>
                </div>
                <p className="text-2xl font-bold text-foreground mt-2">{upgrades}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t('subscription.downgrades')}</p>
                  <Badge variant="warning" size="sm">{t('subscription.period')}</Badge>
                </div>
                <p className="text-2xl font-bold text-foreground mt-2">{downgrades}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t('subscription.trialConversions')}</p>
                  <Badge variant="primary" size="sm">{t('subscription.period')}</Badge>
                </div>
                <p className="text-2xl font-bold text-foreground mt-2">{(trialConversionRate * 100).toFixed(2)}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t('subscription.arpu')}</p>
                  <Badge variant="secondary" size="sm">{t('subscription.period')}</Badge>
                </div>
                <p className="text-2xl font-bold text-foreground mt-2">${formatMoney(arpu)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Revenue by Plan */}
          <Card>
            <CardHeader>
              <CardTitle>{t('subscription.revenueByPlan')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Table striped hoverable>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('subscription.planName')}</TableHead>
                    <TableHead className="text-right">{t('subscription.subscribers')}</TableHead>
                    <TableHead className="text-right">{t('subscription.revenue')}</TableHead>
                    <TableHead className="text-right">{t('subscription.percentOfTotal')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {revenueByPlan.length === 0 ? (
                    <TableEmpty colSpan={4} message={t('subscription.noRevenueData')} />
                  ) : (
                    revenueByPlan.map((plan: any, i: number) => {
                      const pct = totalRevenue > 0
                        ? ((plan.revenue ?? 0) / totalRevenue * 100).toFixed(1)
                        : '0.0';
                      return (
                        <TableRow key={plan.plan_id || plan.plan_name || i}>
                          <TableCell className="font-medium text-foreground">
                            {String(plan.plan_name ?? '--')}
                          </TableCell>
                          <TableCell className="text-right">
                            {plan.subscribers ?? 0}
                          </TableCell>
                          <TableCell className="text-right">
                            ${formatMoney(plan.revenue)}
                          </TableCell>
                          <TableCell className="text-right">
                            {pct}%
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
});

export default SubscriptionAnalyticsPage;
